/**
 * "Search for a Fach" in the bookings overview.
 *
 * The booking search box is free text over booking number, address, customer
 * and locker. A bare `11` therefore also matches every booking number
 * containing 11, which buries the two rows an operator actually wanted — and
 * the whole reason to search by Fach is the narrow question "who had this
 * locker, and what happened in it?".
 *
 * Prefixing the number switches the box to an exact locker lookup:
 *   "Fach 11", "Schließfach 11", "Locker 11", "Fach #11" → locker 11 only.
 *
 * Both languages are accepted whatever the UI language is, so a link built in
 * one locale keeps working after a language switch, and so does a query typed
 * from muscle memory.
 */
import { hasLockerUseCase } from '../common/lockerUseCases';

const LOCKER_QUERY_PATTERN =
    /^(?:fach|schließfach|schliessfach|locker|compartment)\s*#?\s*(\d+)$/i;

/**
 * The locker number a query asks for, or `null` when it is not a locker
 * query and normal free-text search applies.
 */
export const parseLockerQuery = (query: string): number | null => {
    const match = LOCKER_QUERY_PATTERN.exec(query.trim());
    if (!match) return null;

    const door = Number(match[1]);
    return Number.isFinite(door) ? door : null;
};

/**
 * Does this booking sit in the locker the query asked for?
 *
 * Compares numerically on purpose. `booking.door` is typed `number | null`
 * and arrives from a `$queryRawUnsafe` projection of an `Int` column, so it
 * should be a number — but that has not been observed on a real response,
 * and the surrounding free-text search never relied on it (it calls
 * `.toString()`). A strict `===` would therefore be a new assumption whose
 * failure mode is silent: an exact-locker search that always finds nothing,
 * which is the symptom this feature exists to fix.
 */
export const bookingIsInLocker = (
    door: number | string | null | undefined,
    locker: number,
): boolean => {
    if (door === null || door === undefined || door === '') return false;
    return Number(door) === locker;
};

/**
 * The query text for a locker, used to seed the search box from a `?door=`
 * deep link. `prefix` comes from i18n so the box reads in the UI language.
 */
export const buildLockerQuery = (prefix: string, door: number | string) =>
    `${prefix} ${door}`;

/**
 * Does this tenant's booking list carry a locker column at all?
 *
 * `BookingTable` decides whether to render the column from it, and
 * `BookingsPage` decides from the same answer whether to advertise the
 * `Fach 11` search form in its placeholder — a room-rental tenant should not
 * be offered a hint for a concept it does not have.
 *
 * Answered positively from the tenant's services ("does any use case live in
 * lockers?", `LOCKER_USE_CASES`) — the earlier "not apartment, minus named
 * tenants" spelling leaked the column to venue-only tenants (innodom review
 * 1.1/1.2). No tenant is special-cased by name any more (demo qualifies
 * through its own locker services). While the config is still loading
 * (`useCases` undefined) the column stays hidden, so room tenants never see
 * it flash in.
 */
export const tenantHasLockerColumn = (
    useCases: string[] | undefined,
): boolean => hasLockerUseCase(useCases);
